import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import PrimaryButton from "../../../Components/Buttons/primary-button";

import { useModalContext } from "../../../Contexts/ModalContext";

import { formatCurrency } from "../../../Utils/helpers";
import { modalVariants } from "../../../Utils/constants";

import { buildAddressLines, fromCheckResponseAddress } from "../../../Pages/Cash Transaction Pages/mail-check-utils";

import { CheckCircleWithShadow } from "../../../Assets/Icons";

import "./mail-check.scss";

const generateReferenceNumber = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789";
  let suffix = "";
  for (let i = 0; i < 6; i += 1) {
    suffix += chars[Math.floor(Math.random() * chars.length)];
  }
  return `CHK-${suffix}`;
};

export default function renderMailCheckOnTheWay(modalContext, props) {
  const { updateModalComponent, setModalVariant, setModalClassName, setModalTitle, setModalAriaLabel } = modalContext;

  updateModalComponent(() => <MailCheckOnTheWay {...props} />);
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("mail-check-modal mail-check-success-modal hide-header");
  setModalTitle("");
  setModalAriaLabel?.("Your check is on the way");
}

const MailCheckOnTheWay = ({ cashAmount, mailingAddress, arrival, checkResponse }) => {
  const navigate = useNavigate();
  const modalContext = useModalContext();

  const referenceNumber = useMemo(
    () => (checkResponse?.check_id ? `CHK-${checkResponse.check_id}` : generateReferenceNumber()),
    [checkResponse],
  );

  const amount = useMemo(() => {
    const responseAmount = parseFloat(checkResponse?.amount);
    return formatCurrency(isNaN(responseAmount) ? cashAmount : responseAmount);
  }, [checkResponse, cashAmount]);

  const vendorFees = useMemo(() => {
    const fees = parseFloat(checkResponse?.vendor_fees);
    return isNaN(fees) ? null : fees;
  }, [checkResponse]);

  const mailedToAddress = useMemo(
    () => fromCheckResponseAddress(checkResponse?.address, mailingAddress),
    [checkResponse, mailingAddress],
  );

  const recipientName = `${mailedToAddress?.firstName ?? ""} ${mailedToAddress?.lastName ?? ""}`.trim();
  const addressLines = useMemo(() => buildAddressLines(mailedToAddress), [mailedToAddress]);

  // Return to the previous screen however this success modal is dismissed
  // (Done button, backdrop click, or Escape key).
  useEffect(() => {
    modalContext.updateModalOnCloseCallBack(() => navigate(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDone = () => modalContext.onModalClose();

  return (
    <div className="modal-dialog-content mcs-content">
      <div className="mcs-icon">
        <CheckCircleWithShadow />
      </div>
      <h3 className="mcs-title">Your check is on the way!</h3>

      <div className="mcs-details">
        <div className="mcs-detail-row">
          <span className="mcs-detail-label">Amount</span>
          <span className="mcs-detail-value">{amount}</span>
        </div>
        <div className="mcs-detail-row">
          <span className="mcs-detail-label">Fee</span>
          <span className="mcs-detail-value">{vendorFees ? formatCurrency(vendorFees) : "Free"}</span>
        </div>
        <div className="mcs-detail-row">
          <span className="mcs-detail-label">Estimated Arrival</span>
          <span className="mcs-detail-value">{arrival?.labelWithYear}</span>
        </div>
        <div className="mcs-detail-row">
          <span className="mcs-detail-label">Reference Number</span>
          <span className="mcs-detail-value">{referenceNumber}</span>
        </div>
        <div className="mcs-detail-row">
          <span className="mcs-detail-label">Payment Method</span>
          <span className="mcs-detail-value">USPS mailed check</span>
        </div>
        <div className="mcs-detail-row mcs-detail-row-top">
          <span className="mcs-detail-label">Mailed To</span>
          <span className="mcs-detail-value mcs-detail-value-multiline">
            {recipientName}
            {addressLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </span>
        </div>
      </div>

      <PrimaryButton className="mcs-done-btn" onClick={onDone}>
        Done
      </PrimaryButton>
    </div>
  );
};
